import type { ServiceIncident } from '@/mocks/saSystemHealthCenter';

interface IncidentWithService extends ServiceIncident {
  serviceId: string;
  serviceName: string;
  serviceIcon: string;
}

interface Props {
  incidents: IncidentWithService[];
}

export default function IncidentHistory({ incidents }: Props) {
  if (incidents.length === 0) {
    return (
      <div className="bg-background-50 border border-background-200 rounded-2xl p-8 text-center">
        <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
          <i className="ri-check-line text-emerald-500 text-xl"></i>
        </div>
        <p className="text-foreground-600 text-sm font-semibold">Sin incidentes registrados</p>
        <p className="text-foreground-400 text-[11px] mt-1">Todos los servicios funcionan correctamente.</p>
      </div>
    );
  }

  const groupedByDate: Record<string, IncidentWithService[]> = {};
  incidents.forEach((inc) => {
    const day = new Date(inc.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    if (!groupedByDate[day]) groupedByDate[day] = [];
    groupedByDate[day].push(inc);
  });

  const severityConfig: Record<string, { bg: string; text: string; border: string; icon: string }> = {
    minor: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'ri-alert-line' },
    major: { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', icon: 'ri-error-warning-line' },
    critical: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'ri-close-circle-line' },
  };

  return (
    <div className="space-y-6">
      {Object.entries(groupedByDate).map(([day, dayIncidents]) => (
        <div key={day}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center">
              <i className="ri-calendar-line text-foreground-500 text-sm"></i>
            </div>
            <p className="text-foreground-700 text-xs font-semibold capitalize">{day}</p>
            <span className="text-foreground-400 text-[10px]">
              {dayIncidents.length} incidente{dayIncidents.length > 1 ? 's' : ''}
            </span>
          </div>
          <div className="relative pl-5 border-l-2 border-background-200 ml-3.5 space-y-4">
            {dayIncidents.map((incident) => {
              const sc = severityConfig[incident.severity] || severityConfig.minor;
              return (
                <div key={incident.id} className="relative">
                  {/* Timeline dot */}
                  <div className="absolute -left-[25px] top-1.5">
                    <div className={`w-3 h-3 rounded-full ${incident.resolved ? 'bg-background-300' : sc.text === 'text-red-700' ? 'bg-red-500' : 'bg-amber-500'} ${!incident.resolved ? 'animate-pulse' : ''}`}></div>
                  </div>

                  <div className={`${sc.bg} border ${sc.border} rounded-xl p-4`}>
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <i className={`${incident.serviceIcon} ${sc.text} text-sm`}></i>
                        <div>
                          <p className={`text-xs font-bold ${sc.text}`}>{incident.title}</p>
                          <p className="text-foreground-400 text-[9px]">{incident.serviceName}</p>
                        </div>
                      </div>
                      <span className="text-foreground-400 text-[9px] shrink-0 whitespace-nowrap">
                        {new Date(incident.date).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-foreground-500 text-[11px] leading-relaxed mb-2 ml-7">{incident.description}</p>
                    <div className="flex items-center gap-4 text-[10px] ml-7">
                      <span className="text-foreground-500">
                        <i className="ri-timer-line mr-1"></i>
                        {incident.duration}
                      </span>
                      <span className="text-foreground-500">
                        <i className={sc.icon + ' mr-1'}></i>
                        {incident.severity === 'minor' ? 'Menor' : incident.severity === 'major' ? 'Mayor' : 'Crítico'}
                      </span>
                      <span className={`font-semibold flex items-center gap-1 ${incident.resolved ? 'text-emerald-600' : 'text-red-600'}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${incident.resolved ? 'bg-emerald-500' : 'bg-red-500'}`}></span>
                        {incident.resolved ? 'Resuelto' : 'Activo'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}